import { RevButton, RevDialog } from "@revmatics/catalyst";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { useEffect, useState } from "react";
import { PlatformItemType } from "../../../../../../../stores/types/profileTypes.tsx";
import {
  PlatformsUsernameState,
  UsernameItem,
} from "../../../../../../../stores/types/brandSocialHandlesTypes.tsx";
import { useAppDispatch } from "../../../../../../../stores/hooks/hooks.ts";
import { PlatformWrapper } from "./components/platformWrapper/PlatformWrapper.tsx";
import { ValidateBodyType } from "../../dataType.ts";
import { validateSocialHandle } from "../../../../../../../stores/thunks/brandSocialHandlesThunk.tsx";

type Props = {
  platforms: PlatformItemType[];
  usernames: PlatformsUsernameState;
  selectPlatform: (platformId: number, oldPlatformId?: number) => void;
  removePlatform: (platformId: number) => void;
  addUsernameSection: (platformId: number) => void;
  removeUsername: (platformId: number, usernameId: number) => void;
  updateUsernameSection: (platformId: number, data: UsernameItem) => void;
  orders: number[];
};

export const PlatformContainer = ({
  platforms,
  usernames,
  selectPlatform,
  removePlatform,
  addUsernameSection,
  removeUsername,
  updateUsernameSection,
  orders,
}: Props) => {
  const dispatch = useAppDispatch();
  const [startPlatform, setStartPlatform] = useState(true);
  const [
    isOpenDialogRemovePlatformSection,
    setIsOpenDialogRemovePlatformSection,
  ] = useState<null | PlatformItemType>(null);

  useEffect(() => {
    if (Object.keys(usernames).length > 0) {
      setStartPlatform(false);
    }
  }, [usernames]);

  const handleSelectPlatform = (platformId: number, oldPlatformId?: number) => {
    selectPlatform(platformId, oldPlatformId);
  };

  const handleRemovePlatform = (platformId: number) => {
    removePlatform(platformId);
  };

  const handleRemovePlatformSection = (platformId: number) => {
    const needConfirm = (usernames[platformId] ?? []).some(
      (item) => item.status === "valid",
    );
    const platform = platforms.find((item) => item.id === platformId);
    if (needConfirm && platform) {
      setIsOpenDialogRemovePlatformSection(platform);
      return;
    }

    handleRemovePlatform(platformId);
  };

  const handleToValidate = (data: ValidateBodyType) => {
    const { platformId, usernameId, value, mode } = data;
    if (!value) {
      updateUsernameSection(platformId, {
        id: usernameId,
        username: value,
        status: "none",
        mode: "new",
      });
      return;
    }
    updateUsernameSection(platformId, {
      id: usernameId,
      username: value,
      status: "loading",
      mode: mode === "new" ? "new" : "edited",
    });
    const platformName = platforms.find((item) => item.id === platformId)?.name;

    if (platformName) {
      dispatch(
        validateSocialHandle({ platform: platformName, username: value }),
      ).then((result) =>
        updateUsernameSection(platformId, {
          id: usernameId,
          username: value,
          status: result.payload.valid
            ? "valid"
            : platformName === "Facebook"
              ? "valid"
              : "failed",
          mode: mode === "new" ? "new" : "edited",
        }),
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {orders.map((itemId) => {
          const keys = Object.keys(usernames);
          return (
            <PlatformWrapper
              key={`platform-wrapper-${itemId}`}
              platforms={platforms}
              selected={itemId}
              handleSelect={(platformId) =>
                handleSelectPlatform(platformId, itemId)
              }
              disabled={keys.map(Number)}
              platformUsernames={usernames[itemId]}
              handleRemove={() => handleRemovePlatformSection(itemId)}
              handleAddInput={() => addUsernameSection(itemId)}
              handleRemoveInput={(usernameId) =>
                removeUsername(itemId, usernameId)
              }
              handleToValidate={handleToValidate}
              displayTrash={
                keys.length > 1 || (keys.length === 1 && startPlatform)
              }
            />
          );
        })}
        {startPlatform && (
          <PlatformWrapper
            platforms={platforms}
            handleSelect={(platformId) => {
              setStartPlatform(false);
              handleSelectPlatform(platformId);
            }}
            disabled={Object.keys(usernames).map(Number)}
            handleRemove={() => setStartPlatform(false)}
            displayTrash={Object.keys(usernames).length > 0}
          />
        )}
      </div>
      <RevButton
        icon={faPlus}
        variant="secondary"
        text="Add platform"
        buttonSize="extraSmall"
        handleClick={() => setStartPlatform(true)}
        disabled={startPlatform}
      />
      <RevDialog
        isOpen={!!isOpenDialogRemovePlatformSection}
        title={`Remove platform section`}
        setIsOpen={() => setIsOpenDialogRemovePlatformSection(null)}
        btnList={[
          <RevButton
            text="Cancel"
            variant="secondary"
            handleClick={() => setIsOpenDialogRemovePlatformSection(null)}
            key="platform-section-cancel"
          />,
          <RevButton
            text="Remove"
            variant="primary"
            handleClick={() => {
              if (isOpenDialogRemovePlatformSection) {
                removePlatform(isOpenDialogRemovePlatformSection.id);
              }
              setIsOpenDialogRemovePlatformSection(null);
            }}
            key="platform-section-remove"
          />,
        ]}
      >
        <p className="typography-body-sm text-monochrome-900">
          Are you sure you want to remove{" "}
          {isOpenDialogRemovePlatformSection?.name} usernames?
        </p>
      </RevDialog>
    </div>
  );
};
